import { useState } from 'react';
import { Dialog } from './ui/Dialog';

interface Props {
  onCancel: () => void;
  onSubmit: (reason: string) => Promise<void>;
}

/**
 * Reporter (or admin) clicks "Not resolved" on a ticket in a Pending review
 * state. This dialog collects what's still not working; submitting re-opens
 * the ticket and posts the text as a comment in the activity stream.
 * No email goes out on re-open — the reopener can re-engage through the
 * normal ticket workflow.
 */
export function NotResolvedDialog({ onCancel, onSubmit }: Props) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const trimmed = reason.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(trimmed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to re-open ticket.');
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => { if (!o && !submitting) onCancel(); }}
      title="Re-open ticket"
      description="Describe what's still not working. The ticket will be moved back to Open and your note will be added to the activity. No email is sent."
      footer={
        <>
          <button
            onClick={onCancel}
            disabled={submitting}
            type="button"
            className="px-3 py-1.5 rounded-md border border-slate-300 text-sm hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={() => void submit()}
            disabled={submitting || reason.trim().length === 0}
            type="button"
            className="px-3 py-1.5 rounded-md bg-rose-600 text-white text-sm hover:bg-rose-700 disabled:opacity-60"
          >
            {submitting ? 'Re-opening…' : 'Re-open ticket'}
          </button>
        </>
      }
    >
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={6}
        autoFocus
        placeholder="What's still broken? What did you try that didn't work?"
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
    </Dialog>
  );
}
